import React, { useState, useEffect, useCallback } from "react";
import { Container, Tooltip } from "reactstrap";
import { DataTable, IconAvatar } from "../../components/Common/PageWidgets";
import EmptyState from "../../components/Common/EmptyState";
import SecondaryButton from "../../components/Common/SecondaryButton";
import { useNavigate } from "react-router-dom";

//Import API
import endpoints from "../../api/endpoints";
import { get, patch } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import notificationIcon from "../../assets/images/user-dashboard/notification.svg";
import checkIcon from "../../assets/images/user-dashboard/Open Mail Icon Verified.svg";
import readIcon from "../../assets/images/user-dashboard/Open Mail Icon.svg";

/* -------------------------------------------------------------------------
 * Notification Types and Icons
 * ---------------------------------------------------------------------- */

const NOTIFICATION_ICONS = {
  ADMIN_MINI_GAME_SETUP_UPDATED: { icon: notificationIcon, tone: "blue" },
  ADMIN_RESTAURANT_CREDENTIALS_ATTACHED: { icon: notificationIcon, tone: "teal" },
  ADMIN_NEW_RESTAURANT_ADDED: { icon: notificationIcon, tone: "orange" },
  points_earned: { icon: notificationIcon, tone: "teal" },
  voucher_redeemed: { icon: notificationIcon, tone: "orange" },
  referral_bonus: { icon: notificationIcon, tone: "blue" },
  system: { icon: notificationIcon, tone: "grey" },
};

/* -------------------------------------------------------------------------
 * Notifications Table Columns
 * ---------------------------------------------------------------------- */

const notificationColumns = (onView, onMarkAsRead, tooltipOpen, toggleTooltip, onRowClick) => [
  {
    key: "title",
    label: "Title",
    width: "2.5fr",
    render: (row) => {
      const { icon, tone } = NOTIFICATION_ICONS[row.notification_type] || NOTIFICATION_ICONS.system;
      return (
        <span
          className="data-table__row-label"
          style={{ cursor: 'pointer' }}
          onClick={() => onRowClick && onRowClick(row)}
        >
          <IconAvatar icon={icon} tone={tone} size={32} />
          <span
            className={row.read ? "" : "notification-title--unread"}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              lineHeight: '1.4',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = '#fd7e14'}
            onMouseLeave={(e) => e.target.style.color = ''}
          >
            {row.title}
          </span>
        </span>
      );
    },
  },
  {
    key: "message",
    label: "Message",
    width: "3fr",
    render: (row) => {
      const message = row.message || "-";
      return (
        <>
          <span
            id={`message-tooltip-${row.id}`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              maxWidth: '100%',
              cursor: 'pointer',
              lineHeight: '1.4',
            }}
            onClick={() => onRowClick && onRowClick(row)}
          >
            {message}
          </span>
          <Tooltip
            placement="top"
            isOpen={tooltipOpen?.[row.id] || false}
            target={`message-tooltip-${row.id}`}
            toggle={() => toggleTooltip?.(row.id)}
          >
            {message}
          </Tooltip>
        </>
      );
    },
  },
  {
    key: "timeAgo",
    label: "Date & Time",
    width: "1.2fr",
    render: (row) => (
      <span 
        style={{ 
          display: 'block',
          fontSize: '0.875rem',
          color: '#666',
          cursor: 'pointer'
        }}
        onClick={() => onRowClick && onRowClick(row)}
      >
        {row.timeAgo}
      </span>
    ),
  },
  {
    key: "status",
    label: "",
    width: "0.5fr",
    align: "center",
    render: (row) => (
      <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
        {!row.read && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead && onMarkAsRead(row.id);
            }}
            style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
            aria-label="Mark as read"
          >
            <img src={checkIcon} alt="Mark as read" style={{ width: 18, height: 18 }} />
          </button>
        )}
        {row.read && (
          <img src={readIcon} alt="Read" style={{ width: 18, height: 18 }} />
        )}
      </div>
    ),
  },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "read", label: "Read" },
];

/* -------------------------------------------------------------------------
 * NotificationsPage
 * ---------------------------------------------------------------------- */

const NotificationsPage = () => {
  document.title = "Notifications | Omaha Food Lovers";
  const navigate = useNavigate();

  // Tooltip state
  const [tooltipOpen, setTooltipOpen] = useState({});

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [loadingMoreNotifications, setLoadingMoreNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentFilter, setCurrentFilter] = useState("all"); // all, unread, read

  // Configure toastr options for notifications
  const showNotificationToastr = (message, type = "info") => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 3000,
      closeButton: true,
      progressBar: true,
      newestOnTop: true,
    };
    
    if (type === "success") {
      toastr.success(message);
    } else if (type === "error") {
      toastr.error(message);
    } else {
      toastr.info(message);
    }
  };

  // Fetch unread count from API
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await get(endpoints.unreadCount);
      if (response.data.success) {
        setUnreadCount(response.data.data.count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  // Handle filter change
  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = useCallback((newFilter) => {
    setCurrentFilter(newFilter);
    setCurrentPage(1);
    setNotifications([]);
    setHasMoreNotifications(true);
  }, []);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (page = 1, isLoadMore = false) => {
    const setLoading = isLoadMore ? setLoadingMoreNotifications : setLoadingNotifications;
    
    setLoading(true);
    try {
      // Determine which endpoint to use based on filter
      let endpoint;
      if (currentFilter === "unread") {
        endpoint = `${endpoints.notificationsUnread}&page=${page}&page_size=20`;
      } else if (currentFilter === "read") {
        endpoint = `${endpoints.notificationsRead}&page=${page}&page_size=20`;
      } else {
        endpoint = `${endpoints.notificationsAll}&page=${page}&page_size=20`;
      }
      
      const response = await get(endpoint);
      
      if (response.data.success) {
        const notificationsData = response.data.data;
        const newNotifications = notificationsData.data || [];
        
        // Transform API response to match component's expected format
        const transformedNotifications = newNotifications.map((notif) => ({
          id: notif.id,
          type: notif.type || "system",
          notification_type: notif.notification_type || "system",
          title: notif.data?.title || notif.notification_type?.replace(/_/g, ' ') || "Notification",
          message: notif.data?.message || "",
          read: notif.is_read || false,
          timeAgo: formatTimeAgo(notif.created_at),
          createdAt: notif.created_at,
          url: notif.data?.url || notif.data?.redirect_url || null,
        }));
        
        if (isLoadMore) {
          setNotifications((prev) => [...prev, ...transformedNotifications]);
        } else {
          setNotifications(transformedNotifications);
          // Update unread count from meta if available
          setUnreadCount(notificationsData.unread_count || 0);
        }
        
        setCurrentPage(notificationsData.current_page || 1);
        setHasMoreNotifications(
          notificationsData.current_page < notificationsData.last_page
        );
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      if (!isLoadMore) {
        setNotifications([]);
      }
    } finally {
      setLoading(false);
    }
  }, [currentFilter]);

  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle notification row click for redirection
  const handleNotificationClick = (notification) => {
    const notificationUrl = notification.url || notification.redirect_url || null;

    // Mark as read when clicked
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Redirect to the URL from notification data if available
    if (notificationUrl) {
      // Check if it's an external URL or internal route
      if (/^https?:\/\//i.test(notificationUrl)) {
        // Full URL - navigate in the current tab
        window.location.href = notificationUrl;
      } else {
        // Internal route - navigate using React Router
        navigate(notificationUrl);
      }
    } else {
      // Fallback to notification type-based navigation for backward compatibility
      switch (notification.notification_type) {
        case 'points_earned':
          navigate('/wallet');
          break;
        case 'voucher_redeemed':
          navigate('/vouchers');
          break;
        case 'referral_bonus':
          navigate('/wallet');
          break;
        default:
          // For other notifications without URL, do nothing or show a modal
          console.log('Notification clicked:', notification);
      }
    }
  };

  // Fetch notifications on mount and when filter changes
  useEffect(() => {
    fetchUnreadCount();
    fetchNotifications(1, false);
  }, [fetchNotifications, fetchUnreadCount, currentFilter]);

  // Infinite scroll handler
  const handleLoadMoreNotifications = useCallback(() => {
    if (hasMoreNotifications && !loadingMoreNotifications) {
      fetchNotifications(currentPage + 1, true);
    }
  }, [hasMoreNotifications, loadingMoreNotifications, currentPage, fetchNotifications]);

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      if (notificationId === "all") {
        // Mark all as read using the read-all endpoint
        await patch(endpoints.markAllNotificationsRead);
        setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
        setUnreadCount(0);
        showNotificationToastr("All notifications marked as read", "success");
      } else {
        // Mark single notification as read using new endpoint with PATCH
        const endpoint = endpoints.markNotificationRead.replace("{{NOTIFICATION_ID}}", notificationId);
        await patch(endpoint);
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId ? { ...notif, read: true } : notif
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        showNotificationToastr("Notification marked as read", "success");
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      const errorMessage = extractErrorMessage(err);
      showNotificationToastr(errorMessage, "error");
    }
  };

  return (
    <div className="page-content">
      <Container fluid style={{ paddingBottom: 0 }}>
        <div className="vouchers-page__header">
          <h1 className="vouchers-page__title">Notifications</h1>

          <div className="vouchers-page__filters" role="tablist" aria-label="Filter notifications">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={filter.id === currentFilter}
                className={`vouchers-page__filter-btn ${
                  filter.id === currentFilter ? "vouchers-page__filter-btn--active" : ""
                }`}
                onClick={() => handleFilterChange(filter.id)}
              >
                {filter.label === "Unread" && unreadCount > 0 ? `Unread (${unreadCount})` : filter.label}
              </button>
            ))}
          </div>
        </div>

        {unreadCount > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
            <SecondaryButton
              label="Mark all as read"
              variant="orange"
              icon={checkIcon}
              onClick={() => handleMarkAsRead("all")}
            />
          </div>
        )}

        <div className="activity-list">
          {loadingNotifications ? (
            <DataTable
              columns={notificationColumns(null, handleMarkAsRead, tooltipOpen, toggleTooltip, handleNotificationClick)}
              rows={[]}
              getRowKey={(row) => row.id}
              loading={true}
              className="notifications-table"
            />
          ) : notifications && notifications.length > 0 ? (
            <DataTable
              columns={notificationColumns(null, handleMarkAsRead, tooltipOpen, toggleTooltip, handleNotificationClick)}
              rows={notifications}
              getRowKey={(row) => row.id}
              loadingMore={loadingMoreNotifications}
              onLoadMore={handleLoadMoreNotifications}
              hasMore={hasMoreNotifications}
              className="notifications-table"
            />
          ) : (
            <EmptyState
              title="No notifications yet"
              message="You're all caught up! Check back later for new notifications."
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default NotificationsPage;
